/**
 * Theme hues — the colours DaisyUI has no token for, derived from the theme.
 *
 * DaisyUI's semantic tokens cover red (error), amber (warning), green (success),
 * blue (info) and grey (neutral). A spreadsheet that paints "on hold" rows
 * orange or "VIP" rows pink has no token to land on, and a literal colour
 * (`#f97316`) is wrong the moment the app's theme changes.
 *
 * So each hue here is the theme's own `error` colour with only the hue turned:
 * `oklch(from var(--color-error) l c 50)`. It keeps that theme's lightness and
 * saturation — muted on pastel or nord, vivid on acid, darker on dark themes —
 * and follows a theme switch like any DaisyUI token. `error` is the reference
 * because it is the most consistent status colour across DaisyUI's themes:
 * mid-lightness everywhere, with a chroma that tracks how vivid the theme is
 * (warning ranges from near-white to brown). Its `-content` colour is designed
 * to read on that lightness, so each hue borrows its lightness, without its
 * tint (a red-tinted text on a pink badge reads as an error).
 *
 * The rules carry one extra selector so they win over DaisyUI's `.badge` and
 * the status sheet's `[data-g-status]` base whatever order the sheets load in.
 *
 * A hue is used exactly like a semantic token: `badge-orange`, a colorMap value
 * `"orange"`, a row status `orange`, a kanban column `color="orange"`.
 *
 * Declared on `:root` AND `[data-theme]`: a custom property that references
 * another is resolved where it is declared, so a nested `data-theme` scope (the
 * builder previews themes on a wrapper) would otherwise inherit the root theme's
 * hues instead of its own.
 */
export const HUE_TOKENS = ['orange', 'pink', 'purple', 'teal'];

// OKLCH hue angles.
const HUE_ANGLE = { orange: 50, pink: 350, purple: 305, teal: 190 };

// Chroma cap: a theme as vivid as acid (error c .29) would push an orange out
// of gamut, and the browser clips it back to red.
const MAX_CHROMA = 0.2;

const STYLE_ID = 'g-hue-styles';

export const HUE_CSS = [
  `:root,[data-theme]{${HUE_TOKENS.map(h =>
    `--color-${h}:oklch(from var(--color-error) l min(c, ${MAX_CHROMA}) ${HUE_ANGLE[h]});--color-${h}-content:oklch(from var(--color-error-content) l 0 0)`).join(';')}}`,
  ...HUE_TOKENS.map(h => `.badge.badge-${h}{--badge-color:var(--color-${h});--badge-fg:var(--color-${h}-content)}`),
  ...HUE_TOKENS.map(h => `[data-g-status][data-g-status="${h}"]{--g-tint:var(--color-${h})}`),
].join('\n');

/** Inject the hue stylesheet once per document (keyed on the DOM: rollup emits
 *  one bundle per src/ directory, each with its own copy of this module). */
export function ensureHueStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = HUE_CSS;
  document.head.appendChild(style);
}
